import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useUrlParams } from '../utils/url';
import { useCallback } from 'react';
import { apiPost, apiPatch } from '../utils/api';

export enum VerificationStatus {
  NOT_VERIFIED = 'Verify',
  VERIFIED = 'Verified',
  FAILED = 'Failed',
}

interface VerificationContextType {
  verificationLogId: string | null;
  verificationStatus: VerificationStatus;
  isVerified: boolean;
  handleVerify: () => Promise<void>;
}

const VerificationContext = createContext<VerificationContextType | undefined>(undefined);

interface VerificationProviderProps {
  children: ReactNode;
}

export const VerificationProvider = ({ children }: VerificationProviderProps) => {
  const params = useUrlParams();
  const org_id = params.org_id;
  const patientId = params.id;
  const [ verificationLogId, setVerificationLogId ] = useState<string | null>(null);
  const [ verificationStatus, setVerificationStatus ] = useState<VerificationStatus>(VerificationStatus.NOT_VERIFIED);
  const isVerified = verificationStatus === VerificationStatus.VERIFIED;

  const initializeVerification = useCallback(async () => {
    try {
      if (!org_id || !patientId) return;

      const verificationResponse = await apiPost(`/api/${org_id}/patients/${patientId}/log-verification`, {
        userAgent: window.navigator.userAgent,
      });

      const verificationData = await verificationResponse.json();

      if (!verificationData.error) {
        setVerificationLogId(verificationData);
      }
    } catch (error) {
      console.error("Error initializing verification:", error);
      setVerificationStatus(VerificationStatus.FAILED);
    }
  }, [ org_id, patientId ]);

  const handleVerify = async () => {
    try {
      if (!verificationLogId || !org_id || !patientId) return;

      const response = await apiPatch(`/api/${org_id}/patients/${patientId}/log-verification`, {
        verificationLogId
      });

      if (response.ok) {
        setVerificationStatus(VerificationStatus.VERIFIED);
      } else {
        setVerificationStatus(VerificationStatus.FAILED);
      }
    } catch (error) {
      console.error("Error verifying patient:", error);
      setVerificationStatus(VerificationStatus.FAILED);
    }
  };

  useEffect(() => {
    if (org_id && patientId) {
      initializeVerification();
    }
  }, [ org_id, patientId, initializeVerification ]);

  // reset to "Verify" after 3s
  useEffect(() => {
    if (verificationStatus === VerificationStatus.FAILED) {
      setTimeout(() => {
        setVerificationStatus(VerificationStatus.NOT_VERIFIED);
      }, 3000);
    }
  }, [ verificationStatus ]);

  return (
    <VerificationContext.Provider
      value={{
        verificationLogId,
        verificationStatus,
        isVerified,
        handleVerify,
      }}
    >
      {children}
    </VerificationContext.Provider>
  );
};

export const useVerification = (): VerificationContextType => {
  const context = useContext(VerificationContext);

  if (context === undefined) {
    throw new Error('useVerification must be used within a VerificationProvider');
  }

  return context;
};
